import { DownOutlined, FileExcelOutlined, FilePdfOutlined, PrinterOutlined } from "@ant-design/icons";
import { Card, Spin, Alert, Progress, Button, Dropdown, Menu } from "antd";
import jsPDF from "jspdf";
import "jspdf-autotable";
import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { getBalanceSheet } from "./account.api";
import "./account.css";

const formatAmount = (value) => {
  if (value == null || value === "") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return num.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

const formatAmountWithSign = (value) => {
  const num = value == null ? 0 : Number(value);
  if (Number.isNaN(num)) return { text: "—", isNegative: false };
  const isNegative = num < 0;
  return {
    text: (isNegative ? "−" : "") + Math.abs(num).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
    isNegative,
  };
};

const getAccountType = (section, accountName) => {
  const name = (accountName || "").toLowerCase();
  if (section === "assets") {
    if (name.includes("cash") || name.includes("inventory") || name.includes("receivable")) return "Current Asset";
    return "Asset";
  }
  if (section === "liabilities") {
    if (name.includes("payable")) return "Current Liability";
    return "Liability";
  }
  if (section === "equity") {
    if (name.includes("capital")) return "Owner's Equity";
    if (name.includes("cost") || name.includes("deduction")) return "Deduction";
    if (name.includes("sales") || name.includes("revenue")) return "Revenue";
    return "Equity";
  }
  return "";
};

const buildTableRows = (data) => {
  const rows = [];
  const fmt = (v) => (v == null || v === "" ? "—" : Number(v).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 }));
  const assets = data?.assets || [];
  const liabilities = data?.liabilities || [];
  const equity = data?.equity || [];
  assets.forEach((r) => rows.push([r.subAccount || "—", fmt(r.balance), "Assets"]));
  rows.push(["Total Assets", fmt(data?.totalAsset), "Assets"]);
  liabilities.forEach((r) => rows.push([r.subAccount || "—", fmt(r.balance), "Liabilities"]));
  rows.push(["Total Liabilities", fmt(data?.totalLiability), "Liabilities"]);
  equity.forEach((r) => rows.push([r.subAccount || "—", fmt(r.balance), "Equity"]));
  rows.push(["Total Equity", fmt(data?.totalEquity), "Equity"]);
  return rows;
};

const exportPDF = (data) => {
  const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  doc.setFontSize(12);
  doc.text("Balance Sheet", 40, 30);
  const headers = [["Account", "Amount"]];
  const tableRows = buildTableRows(data).map((r) => [r[0], r[1]]);
  doc.autoTable({
    head: headers,
    body: tableRows,
    startY: 40,
    theme: "grid",
    headStyles: { fillColor: [0, 0, 0], fontSize: 9, textColor: [255, 255, 255] },
    bodyStyles: { fontSize: 8 },
  });
  doc.save("balance-sheet.pdf");
};

const exportExcel = (data) => {
  const rows = [["Account", "Amount", "Section"], ...buildTableRows(data)];
  const ws = XLSX.utils.aoa_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Balance Sheet");
  XLSX.writeFile(wb, "balance-sheet.xlsx");
};

const handlePrint = () => {
  window.print();
};

const SectionRows = ({ section, rows, totalLabel, totalValue }) => {
  const total = Math.abs(Number(totalValue)) || 1;
  return (
    <>
      {(rows || []).map((item, index) => {
        const bal = Number(item.balance) || 0;
        const pct = total > 0 ? (Math.abs(bal) / total) * 100 : 0;
        const { text, isNegative } = formatAmountWithSign(bal);
        const strokeColor = isNegative ? "#ff4d4f" : "#52c41a";
        return (
          <tr key={(item.subAccount || "") + index}>
            <td>
              <span className="bs-account-name">{item.subAccount || "—"}</span>
              <span className="bs-account-type">{getAccountType(section, item.subAccount)}</span>
            </td>
            <td className={`bs-amount ${isNegative ? "negative" : "positive"}`}>{text}</td>
            <td className="bs-composition-cell">
              <div className="bs-composition-bar-wrap">
                <Progress
                  percent={Math.min(pct, 100)}
                  showInfo={false}
                  strokeColor={strokeColor}
                  trailColor="#f0f0f0"
                  size="small"
                  className="bs-composition-bar"
                />
              </div>
              <span className="bs-composition-pct">{pct.toFixed(1)}%</span>
            </td>
          </tr>
        );
      })}
      <tr className="bs-total-row">
        <td><strong>{totalLabel}</strong></td>
        <td className="bs-amount bs-total-amount">
          <strong>{formatAmount(totalValue)}</strong>
        </td>
        <td className="bs-composition-cell"><span className="bs-composition-pct">100%</span></td>
      </tr>
    </>
  );
};

const BalanceSheet = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const getData = async () => {
      try {
        setLoading(true);
        setError(null);
        const dataFromServer = await getBalanceSheet();
        setData(dataFromServer);
      } catch (err) {
        setError(err.response?.data?.error || err.message || "Failed to fetch balance sheet");
        console.error("Error loading balance sheet:", err);
      } finally {
        setLoading(false);
      }
    };
    getData();
  }, []);

  if (loading) {
    return (
      <div >
        <Card className="balance-sheet-card bs-card-modern">
          <Spin size="large" style={{ display: "block", textAlign: "center", padding: "50px" }} />
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div >
        <Card className="balance-sheet-card bs-card-modern">
          <Alert message="Error" description={error} type="error" showIcon />
        </Card>
      </div>
    );
  }

  const totalAsset = Number(data?.totalAsset) || 0;
  const totalLiability = Number(data?.totalLiability) || 0;
  const totalEquity = Number(data?.totalEquity) || 0;
  const netWorth = totalAsset - totalLiability;
  const isBalanced = Math.abs(netWorth - totalEquity) < 0.01;

  const assets = data?.assets || [];
  const liabilities = data?.liabilities || [];
  const equity = data?.equity || [];

  return (
    <div >
      <Card className="balance-sheet-card bs-card-modern" bordered={false}>
        <div className="bs-header">
          <h1 className="bs-main-title">Balance Sheet</h1>
          <Dropdown
            overlay={
              <Menu>
                <Menu.Item key="pdf" icon={<FilePdfOutlined />} onClick={() => exportPDF(data)}>
                  Download PDF
                </Menu.Item>
                <Menu.Item key="excel" icon={<FileExcelOutlined />} onClick={() => exportExcel(data)}>
                  Download Excel
                </Menu.Item>
                <Menu.Item key="print" icon={<PrinterOutlined />} onClick={handlePrint}>
                  Print
                </Menu.Item>
              </Menu>
            }
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button type="primary" size="small" className="bs-export-btn">
              Download / Print <DownOutlined />
            </Button>
          </Dropdown>
        </div>

        <div className="bs-table-wrap">
          <table className="bs-table">
            <thead>
              <tr>
                <th>ACCOUNT</th>
                <th className="bs-th-amount">AMOUNT</th>
                <th className="bs-th-composition">COMPOSITION</th>
              </tr>
            </thead>
            <tbody>
              <tr className="bs-section-header bs-section-assets">
                <td colSpan={3}>ASSETS</td>
              </tr>
              <SectionRows section="assets" rows={assets} totalLabel="Total Assets" totalValue={data?.totalAsset} />
              <tr className="bs-section-header bs-section-liabilities">
                <td colSpan={3}>LIABILITIES</td>
              </tr>
              <SectionRows section="liabilities" rows={liabilities} totalLabel="Total Liabilities" totalValue={data?.totalLiability} />
              <tr className="bs-section-header bs-section-equity">
                <td colSpan={3}>EQUITY</td>
              </tr>
              <SectionRows section="equity" rows={equity} totalLabel="Total Equity" totalValue={data?.totalEquity} />
            </tbody>
          </table>
        </div>

        <div className="bs-footer">
          <div className="bs-net-worth">
            <span className="bs-net-worth-label">NET WORTH (Assets − Liabilities)</span>
            <span className="bs-net-worth-value">₹{formatAmount(netWorth)}</span>
          </div>
          {isBalanced && <span className="bs-balanced">Balanced ✓</span>}
        </div>
      </Card>
    </div>
  );
};

export default BalanceSheet;
